import { cx } from '@/lib/cx';

/**
 * Typography primitives.
 *
 * v1 set type inline in every stylesheet, which is how it ended up with six
 * font weights (including a stray 650) and eight sizes for what were really
 * four roles. These are those four roles, sized from v1's own measurements.
 */

interface HeadingProps {
  children: React.ReactNode;
  /** 1 = page title, 2 = pane heading, 3 = group label within a pane. */
  level?: 1 | 2 | 3;
  className?: string;
}

export function Heading({ children, level = 2, className }: HeadingProps) {
  const Tag = (['h1', 'h2', 'h3'] as const)[level - 1];
  return (
    <Tag
      className={cx(
        level === 1 && 'text-xl font-bold text-ink',
        level === 2 && 'text-base font-semibold text-ink',
        // v1's section labels: uppercase, 600, 0.04em tracking, secondary text.
        level === 3 && 'text-2xs font-semibold uppercase tracking-caps text-muted',
        className,
      )}
    >
      {children}
    </Tag>
  );
}

/**
 * The small label above a value in a read-only detail pane, and the label on a
 * form field. Uppercase and tracked out so it reads as a key, not as content.
 */
export function Label({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={cx('text-2xs font-medium uppercase tracking-wide text-muted', className)}>
      {children}
    </span>
  );
}

/** Secondary prose: helper text, timestamps, row subtitles. */
export function Muted({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={cx('text-xs text-muted', className)}>{children}</span>;
}

/**
 * An identifier the user might read aloud or type: enrolment number, invoice
 * number, receipt number, roll number, phone number.
 *
 * Rendered in the body face with tabular figures, not a monospace face — v1
 * used no mono anywhere, and introducing it would be a visible change. The
 * tabular figures are the part that matters: they make a column of enrolment
 * numbers align.
 */
export function Code({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={cx('tabular-nums text-muted', className)}>{children}</span>;
}

/** A key/value row for read-only detail panes. */
export function Detail({
  label,
  children,
  className,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  /** For spanning a grid — an address needs both columns. */
  className?: string;
}) {
  return (
    <div className={cx('flex flex-col gap-0.5', className)}>
      <Label>{label}</Label>
      <span className="text-sm text-ink">{children}</span>
    </div>
  );
}
